import { AcGroundingConfigurationEnum, BreakerTypes, ComponentType, DcGroundingConfigurationEnum } from 'models';

import { customIcons } from './custom-icons';

type CustomIconName = (typeof customIcons)[number]['type'];

export type BusGroundingIconName = 'solidlyGroundedBus' | 'impedanceGroundedBus' | 'symmetricallyGroundedBus';

export type ProtectionIconName =
    | 'breakerClosed'
    | 'breakerUnknown'
    | 'fuseCleared'
    | 'fuseUnknown'
    | 'efuseBreakerCleared'
    | 'efuseBreakerUnknown'
    | 'converterUndervoltage'
    | 'converterUnknown';

export type IconName = ComponentType | CustomIconName | 'efuseBreaker' | ProtectionIconName | BusGroundingIconName;

const SOLIDLY_GROUNDED_CONFIGURATIONS: string[] = [
    AcGroundingConfigurationEnum.TN_S,
    AcGroundingConfigurationEnum.TN_C,
    AcGroundingConfigurationEnum.TT,
];

const customIconNames = new Set<string>(customIcons.map(({ type }) => type));

const isCustomIconName = (name: string): name is CustomIconName => customIconNames.has(name);

const isEFuseBreaker = (type: ComponentType, specifications?: unknown): boolean => {
    if (type !== 'breaker') {
        return false;
    }

    const electrical = (specifications as { electrical?: { breakerType?: unknown } } | null | undefined)?.electrical;

    return electrical?.breakerType === BreakerTypes.EFUSE;
};

type EarthingSpecifications = {
    electrical?: {
        earthing?: {
            configuration?: string | null;
        } | null;
    } | null;
};

const resolveBusGroundingIconName = (
    type: ComponentType,
    specifications?: unknown,
): BusGroundingIconName | undefined => {
    if (type !== 'bus') {
        return undefined;
    }

    const configuration = (specifications as EarthingSpecifications | null | undefined)?.electrical?.earthing
        ?.configuration;

    if (configuration === DcGroundingConfigurationEnum.SYMMETRICAL_IMPEDANCE) {
        return 'symmetricallyGroundedBus';
    }

    if (configuration && SOLIDLY_GROUNDED_CONFIGURATIONS.includes(configuration)) {
        return 'solidlyGroundedBus';
    }

    if (configuration === AcGroundingConfigurationEnum.IT) {
        return 'impedanceGroundedBus';
    }

    return undefined;
};

export const resolveIconName = ({
    type,
    custom,
    specifications,
}: {
    type: ComponentType;
    custom?: string | null;
    specifications?: unknown;
}): IconName => {
    if (custom && isCustomIconName(custom)) {
        return custom;
    }

    if (isEFuseBreaker(type, specifications)) {
        return 'efuseBreaker';
    }

    return resolveBusGroundingIconName(type, specifications) ?? type;
};
